<script setup lang="ts">
import CustomButton from '@/components/CustomButton.vue';
import Dialog from '@/components/dialogs/Dialog.vue';
import LinkEmployeesForm from '@/components/forms/employee/LinkEmployeesForm.vue';
import UpsertEmployeeForm from '@/components/forms/employee/UpsertEmployeeForm.vue';
import { useAuthStore } from '@/stores/auth';
import { useEmployeeStore } from '@/stores/employee';
import { useOrganizationStore } from '@/stores/organization';
import type { OrganizationEmployee } from '@/types/employee';
import type { Organization, OrganizationParent } from '@/types/organization';
import { computed, onMounted, ref, useTemplateRef } from 'vue';

const props = defineProps<{
    organization?: Organization;
    employee?: OrganizationEmployee;
    isInsert: boolean;
}>();

const organizationStore = useOrganizationStore();
const authStore = useAuthStore();
const employeeStore = useEmployeeStore();

const currentOrganization = ref<Organization>();
const parentOrganizations = ref<OrganizationParent>();

const isDialogOpen = defineModel<boolean>();
const isUpsertEmployee = ref(true);
const isRootOrganization = computed(() => currentOrganization.value?.parent == null);
const dialogTitle = computed(() => `Medewerker ${props.isInsert ? 'toevoegen' : 'bewerken'}`);

const upsertEmployeeForm = useTemplateRef('upsertEmployeeForm');

onMounted(async () => {
    await initialize();
});

async function initialize(): Promise<void> {
    await fetchCurrentOrganization();
    await fetchParentOrganization();
    await fetchOrganizationEmployees();
}

async function fetchCurrentOrganization(): Promise<void> {
    if (props.organization) {
        currentOrganization.value = props.organization;
    } else {
        currentOrganization.value = authStore.getCurrentOrganization();
    }
}

async function fetchParentOrganization(): Promise<void> {
    if (currentOrganization.value) parentOrganizations.value = await organizationStore.getAllParentOrganizations(currentOrganization.value._id);
}

async function fetchOrganizationEmployees(): Promise<void> {
    if (parentOrganizations.value != null) {
        const ids: string[] = [parentOrganizations.value._id, ...parentOrganizations.value.parents.map((parent) => parent._id)];
        await employeeStore.fetchInParentOrganizations(ids);
    }
}

function closeDialog() {
    isDialogOpen.value = false;
    isUpsertEmployee.value = true;
}
</script>

<template>
    <Dialog v-model="isDialogOpen" :title="dialogTitle" persistent>
        <v-radio-group v-model="isUpsertEmployee" v-if="!isRootOrganization && isInsert">
            <v-radio label="Nieuwe medewerker aanmaken" :value="true" />
            <v-radio :disabled="employeeStore.filteredOrganizationEmployees?.length === 0" label="Bestaande medewerkers toevoegen" :value="false" />
        </v-radio-group>
        <UpsertEmployeeForm
            v-if="isUpsertEmployee"
            @closeDialog="closeDialog()"
            :organization="currentOrganization"
            :organizationEmployee="props.employee"
            ref="upsertEmployeeForm"
        />
        <LinkEmployeesForm v-else @closeDialog="closeDialog()" :organization="currentOrganization" />
        <template #actions v-if="isUpsertEmployee">
            <CustomButton v-if="upsertEmployeeForm?.isEdit" text="Verplaatsen" color="primary" @click="upsertEmployeeForm?.openMoveEmployeeDialog" />
            <v-spacer />
            <CustomButton class="mr-2" @click="upsertEmployeeForm?.upsert" text="Bevestigen" :loading="upsertEmployeeForm?.isLoading" />
            <CustomButton color="red" @click="upsertEmployeeForm?.closeDialog" text="Annuleren" />
        </template>
    </Dialog>
</template>
